import { isEmpty } from "lodash";
import { take, put, select, actionChannel } from "redux-saga/effects";

import client from "../../../api/client/SdsWebApiClient";
import { ProductInfo, SdsAccountInfo } from "../../../api/generated";

import { CONFIGURATION_LOADED } from "../../actions/configurationActions";
import { ADD_METADATA, SET_METADATA, SET_PRODUCT_INFO } from "../../actions/metadataActions";
import { ModelMetadata, ProductInfoMap } from "../../stateTypes/metadataState";

import { getModelId, getCategory, getLanguage, getFrontendSettings, getAccount } from "../selectors";

const defaultTags = [
    "Icon",
    "hide_when_active", // Only used in classic
    // "mask_redirect_menu",
    "STATIC_GROUP",
    // "GUIGroup",
    "DynamicType",
    "property_groups",
    "ProductId",
];

export function* loadMetadataForModel(category: string, modelId: string): any {
    // Skip if we already have metadata for this model.
    const existingMetadata = yield select(state => state.metadata.metadata[modelId]);
    if (existingMetadata) return;

    const language = yield select(getLanguage);
    const frontendSettings = yield select(getFrontendSettings);
    const account: SdsAccountInfo | null = yield select(getAccount);

    const filterTagsKey = "filter_tags";
    const filterTags = frontendSettings?.[filterTagsKey]?.split(",").reduce((result: string[], tag: string) => {
        if (!isEmpty(tag)) {
            result.push(`Filter_${tag}`);
        }

        return result;
    }, []);

    const tags = [...defaultTags, ...filterTags];
    const metadata: ModelMetadata = yield client.getItemMetadataGet(
        category,
        modelId,
        language,
        tags,
        undefined,
        account?.id || "BASE",
        undefined
    );

    yield put({
        type: SET_METADATA,
        modelId,
        metadata,
    });
}

export function* loadMetadataForItems(
    itemIds: string[],
    modelId: string,
    metadataKeys: string[],
    transform?: (metadata: ModelMetadata) => void
): any {
    const category = yield select(getCategory);
    const language = yield select(getLanguage);
    const account: SdsAccountInfo | null = yield select(getAccount);
    const metadata: ModelMetadata = yield client.getItemMetadataGet(
        category,
        modelId,
        language,
        metadataKeys,
        itemIds,
        account?.id || "BASE",
        undefined
    );

    if (transform) transform(metadata);

    yield put({
        type: ADD_METADATA,
        modelId,
        metadata,
    });
}

export function* loadMetadataSaga(): any {
    // Queue the actions to avoid multiple calls if already loading.
    const channel = yield actionChannel(CONFIGURATION_LOADED);
    while (true) {
        yield take(channel);
        const category = yield select(getCategory);
        const modelId = yield select(getModelId);
        yield loadMetadataForModel(category, modelId);
    }
}

export function* loadProductInfoSaga(language: string, localizationId: string, accountId: string | undefined) {
    const response: ProductInfo[] = yield client.productInfoGet(language, localizationId, accountId);
    const productInfo = new ProductInfoMap(response);
    yield put({
        type: SET_PRODUCT_INFO,
        productInfo,
    });
}
